import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, FormField, Grid, Select, Text, TextInput } from "grommet";
import { FormPrevious } from "grommet-icons";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, useDirtyGuard, useScrollToTop } from "../../context";
import { ConfirmDialog, CurrencyInput } from "../../components";
import { formatCents, parseDollarsToCents } from "../../utils/money";
import {
    deleteLoan,
    getFunders,
    getLoan,
    getLoanAudit,
    hardDeleteLoan,
    restoreLoan,
    updateLoan,
} from "../../api/funderAllocation";
import { AuditLogTable } from "./AuditLogTable";
import { AllocationEditor } from "./AllocationEditor";
import { DetailTabs } from "./DetailTabs";
import { Panel } from "./Panel";
import { ctaButtonProps } from "./ctaButton";
import { DetailField } from "./DetailField";

const LOAN_STATUSES = ["Settled", "Discharged"];
const TABS = [
    { key: "allocations", label: "Allocations" },
    { key: "audit", label: "Audit" },
];

const LoanDetailSection = () => {
    const { loanId } = useParams();
    const { tokens, isInGroup } = useAuth();
    const navigate = useNavigate();
    const { confirmLeave } = useDirtyGuard();
    const scrollToTop = useScrollToTop();
    const isAdmin = isInGroup("admin");

    const [activeTab, setActiveTab] = useState("allocations");

    const [loan, setLoan] = useState(null);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(false);
    const [editValue, setEditValue] = useState(null);
    const [saving, setSaving] = useState(false);
    const [confirmingDeleteLoan, setConfirmingDeleteLoan] = useState(false);
    const [confirmingHardDeleteLoan, setConfirmingHardDeleteLoan] =
        useState(false);

    const [funders, setFunders] = useState([]);

    const [audit, setAudit] = useState([]);
    const [auditLoading, setAuditLoading] = useState(true);

    const loadLoan = useCallback(async () => {
        if (!tokens?.idToken) return;
        try {
            const data = await getLoan(tokens.idToken, loanId);
            setLoan(data);
            setEditValue({
                ref: data.ref,
                name: data.name,
                amount: String(data.amountCents / 100),
                status: data.status,
            });
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to load loan");
        }
    }, [tokens, loanId]);

    const loadFunders = useCallback(async () => {
        if (!tokens?.idToken) return;
        try {
            const data = await getFunders(tokens.idToken, {
                showDeleted: false,
            });
            setFunders(data || []);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to load funders");
        }
    }, [tokens]);

    const loadAudit = useCallback(async () => {
        if (!tokens?.idToken) return;
        setAuditLoading(true);
        try {
            const data = await getLoanAudit(tokens.idToken, loanId);
            setAudit(data || []);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to load audit trail");
        } finally {
            setAuditLoading(false);
        }
    }, [tokens, loanId]);

    useEffect(() => {
        loadLoan();
    }, [loadLoan]);
    useEffect(() => {
        loadFunders();
    }, [loadFunders]);
    useEffect(() => {
        if (activeTab === "audit") loadAudit();
    }, [loadAudit, activeTab]);
    useEffect(() => {
        scrollToTop();
    }, [activeTab, scrollToTop]);

    const handleBack = () => {
        if (!confirmLeave()) return;
        navigate("/funder-allocation/loans");
    };

    const handleSaveEdit = async () => {
        const amountCents = parseDollarsToCents(editValue.amount);
        if (
            !editValue.ref ||
            !editValue.name ||
            !amountCents ||
            amountCents <= 0
        )
            return;
        setSaving(true);
        setError(null);
        try {
            const updated = await updateLoan(tokens.idToken, loanId, {
                ref: editValue.ref,
                name: editValue.name,
                amountCents,
                status: editValue.status,
            });
            setLoan(updated);
            setEditing(false);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to save loan");
        } finally {
            setSaving(false);
        }
    };

    const handleCancelEdit = () => {
        setEditValue({
            ref: loan.ref,
            name: loan.name,
            amount: String(loan.amountCents / 100),
            status: loan.status,
        });
        setEditing(false);
    };

    const handleDelete = async () => {
        setConfirmingDeleteLoan(false);
        setError(null);
        try {
            const updated = await deleteLoan(tokens.idToken, loanId);
            setLoan(updated);
            setEditing(false);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to delete loan");
        }
    };

    const handleRestore = async () => {
        setError(null);
        try {
            const updated = await restoreLoan(tokens.idToken, loanId);
            setLoan(updated);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to restore loan");
        }
    };

    const handleHardDelete = async () => {
        setConfirmingHardDeleteLoan(false);
        setError(null);
        try {
            await hardDeleteLoan(tokens.idToken, loanId);
            navigate("/funder-allocation/loans");
        } catch (e) {
            setError(
                e?.response?.data?.detail ||
                    "Failed to permanently delete loan",
            );
        }
    };

    if (!loan) {
        return <Text>{error || "Loading…"}</Text>;
    }

    return (
        <Box gap="medium">
            <Box direction="row" justify="between" align="center">
                <Button
                    icon={<FormPrevious />}
                    a11yTitle="Back to loans"
                    onClick={handleBack}
                />
                <DetailTabs
                    tabs={TABS}
                    active={activeTab}
                    onChange={setActiveTab}
                />
            </Box>
            {error && <Text color="status-critical">{error}</Text>}

            <Panel>
                {editing ? (
                    <Box width="medium" gap="small">
                        <FormField label="Reference" required>
                            <TextInput
                                value={editValue.ref}
                                onChange={(e) =>
                                    setEditValue((v) => ({
                                        ...v,
                                        ref: e.target.value,
                                    }))
                                }
                            />
                        </FormField>
                        <FormField label="Name" required>
                            <TextInput
                                value={editValue.name}
                                onChange={(e) =>
                                    setEditValue((v) => ({
                                        ...v,
                                        name: e.target.value,
                                    }))
                                }
                            />
                        </FormField>
                        <FormField label="Amount" required>
                            <CurrencyInput
                                value={editValue.amount}
                                onChange={(raw) =>
                                    setEditValue((v) => ({ ...v, amount: raw }))
                                }
                            />
                        </FormField>
                        <FormField label="Status">
                            <Select
                                options={LOAN_STATUSES}
                                value={editValue.status}
                                onChange={({ option }) =>
                                    setEditValue((v) => ({
                                        ...v,
                                        status: option,
                                    }))
                                }
                            />
                        </FormField>
                        <Box direction="row" gap="small" wrap>
                            <Button
                                label="Save"
                                {...ctaButtonProps}
                                disabled={saving}
                                onClick={handleSaveEdit}
                            />
                            <Button label="Cancel" onClick={handleCancelEdit} />
                            {loan.isActive && (
                                <Button
                                    label="Delete"
                                    color="status-critical"
                                    onClick={() =>
                                        setConfirmingDeleteLoan(true)
                                    }
                                />
                            )}
                        </Box>
                    </Box>
                ) : (
                    <>
                        <Grid
                            columns={["1fr", "1fr", "1fr"]}
                            rows={["auto", "auto"]}
                            gap="medium"
                            areas={[
                                { name: "ref", start: [0, 0], end: [0, 0] },
                                { name: "name", start: [1, 0], end: [2, 0] },
                                { name: "amount", start: [0, 1], end: [0, 1] },
                                { name: "status", start: [1, 1], end: [1, 1] },
                                {
                                    name: "recordStatus",
                                    start: [2, 1],
                                    end: [2, 1],
                                },
                            ]}
                        >
                            <DetailField
                                gridArea="ref"
                                label="Reference"
                                value={loan.ref}
                            />
                            <DetailField
                                gridArea="name"
                                label="Name"
                                value={loan.name}
                            />
                            <DetailField
                                gridArea="amount"
                                label="Amount"
                                value={formatCents(loan.amountCents)}
                            />
                            <DetailField
                                gridArea="status"
                                label="Status"
                                value={loan.status}
                            />
                            <DetailField
                                gridArea="recordStatus"
                                label="Record status"
                                value={loan.isActive ? "Active" : "Deleted"}
                            />
                        </Grid>
                        <Box
                            direction="row"
                            gap="small"
                            margin={{ top: "small" }}
                            wrap
                        >
                            <Button
                                label="Edit"
                                onClick={() => setEditing(true)}
                            />
                            {!loan.isActive && (
                                <Button
                                    label="Restore"
                                    onClick={handleRestore}
                                />
                            )}
                            {!loan.isActive && isAdmin && (
                                <Button
                                    label="Delete permanently"
                                    color="status-critical"
                                    onClick={() =>
                                        setConfirmingHardDeleteLoan(true)
                                    }
                                />
                            )}
                        </Box>
                    </>
                )}
            </Panel>

            {activeTab === "allocations" ? (
                <AllocationEditor
                    key={loanId}
                    loanId={loanId}
                    loanAmountCents={loan.amountCents}
                    loanIsActive={loan.isActive}
                    funders={funders}
                    isAdmin={isAdmin}
                />
            ) : (
                <Panel>
                    <AuditLogTable loading={auditLoading} entries={audit} />
                </Panel>
            )}

            {confirmingDeleteLoan && (
                <ConfirmDialog
                    title="Delete this loan?"
                    message="It will be hidden from the default list but can be restored later. Blocked if it has any live allocations."
                    onConfirm={handleDelete}
                    onCancel={() => setConfirmingDeleteLoan(false)}
                />
            )}
            {confirmingHardDeleteLoan && (
                <ConfirmDialog
                    title="Permanently delete this loan?"
                    message="This cannot be undone - the record will be removed completely, not just hidden. Blocked if it has any allocation history."
                    confirmLabel="Delete permanently"
                    onConfirm={handleHardDelete}
                    onCancel={() => setConfirmingHardDeleteLoan(false)}
                />
            )}
        </Box>
    );
};

export { LoanDetailSection };
